import { getProductFamilyDetails, IDesignerDetail, pimImageToOpenGraphImage } from '@viabizzuno/models';
import { getLayout } from '@viabizzuno/models/server';
import { LayoutDefault } from '@viabizzuno/ui';
import { getPageProps } from '@viabizzuno/ui/server';
import { asEquatable, IStaticContext, PageProps } from '@websolutespa/bom-core';
import { LabelService } from '@websolutespa/bom-mixer-hooks';
import { getPage, getStaticPathsForSchema } from '@websolutespa/bom-mixer-models/server';
import { LazyLoader, OpenGraph, StructuredData } from '@websolutespa/bom-mixer-ui';
import { BreadcrumbStructuredData } from '@viabizzuno/ui';
import { GetStaticPropsResult } from 'next';

export default function DesignerDetail({ layout, page, params }: PageProps<IDesignerDetail>) {
  return (
    <>
      <LazyLoader components={page.components} />
      <StructuredData />
      <BreadcrumbStructuredData />
      <OpenGraph item={(metaData) => ({
        ...metaData.openGraph,
        image: page.pimJson?.immagine_designer ? pimImageToOpenGraphImage(page.pimJson.immagine_designer) : undefined,
      })} />
    </>
  );
}

DesignerDetail.Layout = LayoutDefault;

export async function getStaticProps(context: IStaticContext): Promise<GetStaticPropsResult<PageProps<IDesignerDetail>>> {
  const id = asEquatable(context.params.id);
  const market = context.params.market;
  const locale = context.params.locale;
  const layout = await getLayout(market, locale);
  const labelService = new LabelService(layout.labels);
  const page = await getPage<IDesignerDetail>('designer_detail', id, market, locale, context);
  /*
  const items = await getHomepages({ market, locale });
  console.log(items.length);
  */
  if (!page) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  const designerProducts = await getProductFamilyDetails({
    market,
    locale,
    sort: 'title',
    limit: 0,
    where: { designers: { in: [page.id] } }
  });

  const components = page.components || [];
  components.push({
    schema: 'block_hero_2cols',
    abstract: page.pimJson.descrizione,
    media: page.pimJson.immagine_designer,
    ...(designerProducts && {
      nav: {
        href: '#products',
        title: labelService.getLabel('block_hero_2cols.anchor.products') as string,
      },
    }),
  });
  components.push({
    schema: 'block_full_image',
    ...(page.pimJson?.foto_progetto_full
      ? { media: { item: page.pimJson.foto_progetto_full } } : page.pimJson?.foto_progetto_sull
        ? { media: { item: page.pimJson.foto_progetto_sull } } : undefined),
  });
  components.push({
    schema: 'block_designer_gallery',
    designer: { ...page },
  });
  components.push({
    schema: 'block_2cols',
    direction: 'right',
    title: labelService.getLabel('designer.projects_2cols.title', { designer: page.title }),
    abstract: page.pimJson.descrizione_collaborazione,
    items: [
      ...(page.pimJson.foto_prodotto_1 ? [{ item: page.pimJson.foto_prodotto_1 }] : []),
      ...(page.pimJson.foto_prodotto_2 ? [{ item: page.pimJson.foto_prodotto_2 }] : []),
    ],
  });
  components.push({
    schema: 'block_carousel_media',
    title: labelService.getLabel('designer.gallery.title'),
    ...(page.pimJson?.gallery ? { items: [page.pimJson?.gallery].flat().map((item) => ({ item: item })) } : { items: [] }),
  });
  components.push({
    schema: 'block_designer_products',
    product: designerProducts,
  });
  components.push({
    schema: 'block_projects_proposition',
    anchor: {
      title: 'Projects',
      hash: 'projects',
    },
  });
  page.components = components;

  const props = await getPageProps({ ...context, layout, page });
  return {
    props,
    /*
    * Next.js will attempt to re-generate the page when a request comes in at most once every 60 seconds
    */
    revalidate: 60 * 60 * 2, // revalidate
  };
}

export async function getStaticPaths() {
  const paths = await getStaticPathsForSchema('designer_detail');
  return {
    paths,
    fallback: 'blocking', // runs before initial render
    // fallback: true, // runs in the background
  };
}
